import {
    useEffect,
    useState,
    type HTMLAttributes,
    type ReactNode,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { cn } from '@rocco/utils/cn';
import { useHydrated } from 'remix-utils';

interface Dimension {
    width?: number | null;
    height?: number | null;
}

interface LazyImageModuleProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    dimension?: Dimension | null;
    rootMargin?: string;
    skeletonClassName?: string;
    /** Index of this module in the list, used to determine eager loading */
    moduleIndex?: number;
    /** Number of modules to render eagerly (default: 2) */
    eagerCount?: number;
    /**
     * Skip CSS `content-visibility:auto` optimisation while keeping
     * IntersectionObserver-based lazy rendering. Use for flex-wrap gallery
     * items where `content-visibility` applies `contain: layout` and breaks
     * the parent flex layout.
     */
    skipContentVisibility?: boolean;
}

/**
 * SSR-aware lazy loading for image modules.
 *
 * Behavior:
 * - SSR: Always renders children (actual images) for SEO - crawlers see full content
 * - Client hydration: Applies intersection observer for below-fold modules only
 * - First N modules (eagerCount): Render immediately without skeleton transition
 * - Below-fold modules: Use CSS content-visibility for rendering optimization
 *
 * This approach balances SEO (full content in initial HTML) with performance
 * (native lazy loading + content-visibility for below-fold content).
 */
const LazyImageModule = ({
    children,
    className,
    dimension,
    rootMargin = '400px',
    skeletonClassName,
    moduleIndex = 0,
    eagerCount = 2,
    skipContentVisibility = false,
    ...rest
}: LazyImageModuleProps) => {
    const isHydrated = useHydrated();
    const isEager = moduleIndex < eagerCount;
    const applyContentVisibility = !isEager && !skipContentVisibility;

    // During SSR or for eager modules: always render children
    // This ensures crawlers see actual image content for SEO
    const [shouldRenderChildren, setShouldRenderChildren] = useState(
        () => !isHydrated || isEager,
    );

    const { ref, inView } = useInView({
        triggerOnce: true,
        rootMargin,
        // Skip observation for eager modules - they render immediately
        skip: isEager,
    });

    useEffect(() => {
        // After hydration, show content when in view or if eager
        if (isHydrated && (inView || isEager)) {
            setShouldRenderChildren(true);
        }
    }, [isHydrated, inView, isEager]);

    const aspectRatio =
        dimension?.width && dimension?.height && dimension.width > 0
            ? dimension.height / dimension.width
            : null;

    // SSR: Always render children for SEO
    // Client: Render based on visibility state
    const renderContent = !isHydrated || shouldRenderChildren;

    return (
        <div
            ref={ref}
            className={cn(
                className,
                applyContentVisibility && '[content-visibility:auto]',
            )}
            style={
                applyContentVisibility
                    ? {
                          // Assumes the image is displayed at full viewport
                          // width on mobile; adjust if used for narrower items.
                          containIntrinsicBlockSize: aspectRatio
                              ? `${(aspectRatio * 100).toFixed(2)}vw`
                              : '300px',
                      }
                    : undefined
            }
            {...rest}
        >
            {renderContent ? (
                children
            ) : (
                <div
                    className={cn(
                        'relative w-full overflow-hidden rounded-2xl bg-gray-100',
                        skeletonClassName,
                    )}
                    style={{
                        paddingBottom: `${Math.max(aspectRatio ?? 0.66, 0.1) * 100}%`,
                    }}
                >
                    <div className="absolute inset-0 animate-pulse bg-gray-100" />
                </div>
            )}
        </div>
    );
};

export default LazyImageModule;
